import {
  colors,
  copyToClipboard,
  date,
  exportFile,
  is,
  Notify,
} from 'quasar'
import { deepUnref } from 'vue-deepunref'
import { gsap } from 'gsap'
import { NotifyError } from 'js/vue/boot/Notify'
import { unref } from 'vue'
import queryString from 'query-string'
import useApiError from 'composables/useApiError'

export const URLSearch = new URLSearchParams(window.location.search)

export const screen = {
  md: () => window.matchMedia('(max-width: 1180px)').matches,
}

export function debounce(time, fn, name) {
  return (...args) => {
    clearTimeout(window[name])
    window[name] = setTimeout(() => {
      fn(...args)
    }, time)
  }
}

export function enableDragScroll(removeEvents) {
  return (e, options) => {
    const domElement = e.currentTarget
    const offset = { x: 0, y: 0 }
    const startPos = { x: 0, y: 0 }

    const onMouseMove = (e) => {
      if (removeEvents.value) {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      domElement.scrollLeft = offset.x + startPos.x - e.clientX
      domElement.scrollTop = offset.y + startPos.y - e.clientY
      return false
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      return false
    }

    const onMouseDown = (e) => {
      removeEvents.value = false
      if (e.button !== 0 || options?.fxIgnore(e)) return
      startPos.x = e.clientX
      startPos.y = e.clientY
      offset.x = domElement.scrollLeft
      offset.y = domElement.scrollTop
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
      return false
    }

    domElement.addEventListener('mousedown', onMouseDown)
  }
}

export function ordenateKey(key) {
  return (a, b) => {
    if (a[key] > b[key]) return 1
    if (a[key] < b[key]) return -1
    return 0
  }
}

export function setHeightInCol(
  wrapper = '.cards-wrapper',
  item = '.kanban-card',
  space = 12
) {
  document.querySelectorAll(wrapper).forEach((i) => {
    const filhos = [...i.querySelectorAll(item)]
    const gap = filhos.length * space
    const height = filhos.reduce((acc, children) => {
      acc += children.getBoundingClientRect().height
      return acc
    }, 0)
    const heightWithGap = height + gap + 16
    gsap.to(i, {
      height: heightWithGap.toFixed(0),
      duration: 0.5,
      ease: 'power1',
    })
  })
}

export async function copyAndAlert(text) {
  try {
    await copyToClipboard(text)
    Notify.create({
      icon: 'check_circle',
      progress: true,
      classes: 'notify-sucess',
      timeout: 2500,
      message: 'Copiado com Sucesso',
    })
  } catch (e) {
    console.log(e)
    NotifyError('Erro ao Copiar')
  }
}

export function FData(value, format = 'DD/MM/YY') {
  if (date.isValid(value)) {
    const dataFormatted =
      value.length > 10 ? value : String(value).replaceAll('-', '/')
    const data = date.formatDate(dataFormatted, format, {
      days: [
        'Domingo',
        'Segunda',
        'Terça',
        'Quarta',
        'Quinta',
        'Sexta',
        'Sábado',
      ],
    })
    return data
  } else {
    return '-'
  }
}

export function FDateBRtoEU(value) {
  if (value) {
    const dia = value.slice(0, 2)
    const mes = value.slice(3, 5)
    const ano = value.slice(6, 10)
    return `${ano}-${mes}-${dia}`
  } else {
    return '-'
  }
}

export function FMoney(value, opts) {
  const val = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    ...opts,
  }).format(value)
  return val.toString() === 'NaN' ? '-' : val
}

export function removeHTMLFromString(v) {
  return v.replaceAll(/<[^>]*>/g, '')
}

export function isEmpty(obj) {
  if (!is.object(obj)) return true
  return Object.keys(obj).length === 0
}

export function FDataAndTime(
  value,
  format = 'DD/MM/YYYY - HH:mm',
  text = 'às'
) {
  if (date.isValid(value)) {
    const dataFormatted =
      value.length > 10 ? value : value.replace('-', '/')
    const data = date.formatDate(dataFormatted, format)
    return `${data}`
  } else {
    return '-'
  }
}

export function compareAndReturnDiff(oldv = {}, newv = {}) {
  const modificado = {}
  if (!oldv || !newv) return null
  Object.entries(unref(newv)).forEach(([key, v]) => {
    const a = oldv[key]
    const b = newv[key]
    if (is.deepEqual(a, b)) return null
    else modificado[key] = v
  })
  return Object.keys(modificado)?.length ? modificado : null
}

export function generateStringFilterFromObject(obj, x) {
  const filtros = Object.entries(obj).map(
    ([key, value]) => `&${key?.trim()}=${value || ''}`
  )
  return x ? filtros.join('') : filtros.join('').replace('&', '?')
}

export function generateObjFilter() {
  const params = {}
  for (const [key, value] of new URLSearchParams(
    window.location.search
  ).entries()) {
    params[key] = value
  }
  return params
}

export function isEmptyData(data) {
  const result = {}
  if (!data) {
    result.isEmpty = true
  } else if (Array.isArray(data) && data.length === 0) {
    result.isEmpty = true
  } else if (
    typeof data === 'object' &&
    Object.keys(data).length === 0 &&
    data.constructor === Object
  ) {
    result.isEmpty = true
  } else {
    result.isEmpty = false
  }
  result.isArray = Array.isArray(data)
  result.isObject = is.object(data)
  return result
}

export function exportTable(columns, rows) {
  const content = [columns.map((col) => wrapCsvValue(col.label))]
    .concat(
      deepUnref(rows).map((row) =>
        columns
          .map((col) =>
            wrapCsvValue(
              typeof col.field === 'function'
                ? col.field(row)
                : row[col.field === void 0 ? col.name : col.field],
              col.format,
              row
            )
          )
          .join(',')
      )
    )
    .join('\r\n')

  const status = exportFile('table-export.csv', content, 'text/csv')

  if (status !== true) {
    NotifyError('Erro ao baixar')
  }
}

// Função auxiliar usada em exportTable
export function wrapCsvValue(val) {
  let v = val === undefined || val === null ? '' : String(val)
  v = v.replace(/"/g, '""')
  return `"${v}"`
}

export async function blobDownloadFile(url, fileName) {
  const image = await fetch(url)
  const imageBlob = await image.blob()
  const imageURL = URL.createObjectURL(imageBlob)

  const anchor = document.createElement('a')
  anchor.href = imageURL
  anchor.download = fileName

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(imageURL)
}

export function calcularDiferenca(data) {
  const dataAtual = new Date()
  const dataRecebida = new Date(data)
  const diferencaEmMS = dataAtual - dataRecebida
  const diferencaEmSegundos = diferencaEmMS / 1000
  const diferencaEmMinutos = diferencaEmSegundos / 60
  const diferencaEmHoras = diferencaEmMinutos / 60

  if (diferencaEmMinutos < 1) {
    return 'Agora'
  } else if (diferencaEmHoras < 1) {
    return `${Math.floor(diferencaEmMinutos)}m atrás`
  } else if (diferencaEmHoras < 24) {
    return `${Math.floor(diferencaEmHoras)}h e ${Math.floor(
      diferencaEmMinutos % 60
    )}m atrás`
  } else {
    const diferencaEmDias = diferencaEmHoras / 24
    return `${Math.floor(diferencaEmDias)}d e ${Math.floor(
      diferencaEmHoras % 24
    )}h atrás`
  }
}

export function returnRGB(v) {
  return v ? `${Object.values(colors.hexToRgb(v))}` : ''
}

export function unique(value, index, self) {
  return self.indexOf(value) === index
}

export function stringifyUrl({ query = {}, url, prefix }) {
  const str = queryString.stringifyUrl(
    {
      url,
      query,
    },
    { skipEmptyString: true, skipNull: true }
  )
  return `${str}`
}

export const EVIDENCIA_STATUS_APROVADA = 'Evidência Aprovada'
export const EVIDENCIA_STATUS_REPROVADA = 'Evidência Reprovada'
export const EVIDENCIA_STATUS_PENDENTE = 'Pendente de Aprovação'

export function setColorStatus(status) {
  switch (status) {
    case EVIDENCIA_STATUS_PENDENTE:
      return 'var(--status-pendente)'
    case EVIDENCIA_STATUS_REPROVADA:
      return 'var(--status-reprovado)'
    case EVIDENCIA_STATUS_APROVADA:
      return 'var(--status-aprovado)'
    default:
      return ''
  }
}

export function setBgStatus(status) {
  switch (status) {
    case EVIDENCIA_STATUS_PENDENTE:
      return 'var(--alert-warning-10)'
    case EVIDENCIA_STATUS_REPROVADA:
      return 'var(--alert-error-10)'
    case EVIDENCIA_STATUS_APROVADA:
      return 'var(--alert-success-10)'
    default:
      return ''
  }
}

export function setNomeStatus(status, t) {
  if (status === EVIDENCIA_STATUS_PENDENTE) {
    return t('pendingValidationStatus')
  } else if (status === EVIDENCIA_STATUS_REPROVADA) {
    return t('rejectedEvidence')
  } else if (status === EVIDENCIA_STATUS_APROVADA) {
    return t('approvedEvidence')
  }
}

export function setIconStatus(status) {
  switch (status) {
    case EVIDENCIA_STATUS_PENDENTE:
      return 'schedule'
    case EVIDENCIA_STATUS_REPROVADA:
      return 'close'
    case EVIDENCIA_STATUS_APROVADA:
      return 'check'
    default:
      return ''
  }
}

// statusEvidencia e tooltipProps já eram exportados separadamente
export const statusEvidencia = (status) => ({
  pendente: status === EVIDENCIA_STATUS_PENDENTE,
  reprovada: status === EVIDENCIA_STATUS_REPROVADA,
  aprovada: status === EVIDENCIA_STATUS_APROVADA,
})

export const tooltipProps = {
  anchor: 'top middle',
  self: 'bottom middle',
  offset: [10, 10],
  class: 'rounded-3xl text-headline-3 !px-12 shadow-lg',
}

export const tooltipPropsSidebar = {
  anchor: 'top middle',
  self: 'bottom middle',
  offset: [8, 8],
  class: 'bg-[#1B1D26] text-headline-3 !text-white !px-12 rounded',
}

export const axiosUploadProgress = (loading) => ({
  onUploadProgress: (event) => {
    if (event.lengthComputable) {
      const percent = ((event.loaded / event.total) * 100).toFixed()
      loading = percent
    }
  },
})

export function newFormData(obj) {
  const formData = new FormData()
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      value.forEach((i) => formData.append(key, i.file))
    } else formData.append(key, value)
  })
  return formData
}

export async function fetchAllPages({
  getFun,
  prop,
  loading,
  params,
}) {
  let nextPage = 1

  try {
    while (nextPage) {
      const r = await getFun({ page: nextPage, ...params })

      const results = prop.value || []
      prop.value = [...results, ...r.results]

      nextPage = r.next
        ? queryString.parseUrl(r.next).query?.page
        : null
    }
    return prop.value
  } catch (error) {
    useApiError(error)
  }
}

export function useAssets(url) {
  const staticUrl = window.context?.staticUrl || '/static/'
  return url.startsWith('/static/') ? staticUrl + url.slice('/static/'.length) : url
}
